import CreateButton from "../components/CreateButton"
import { useEffect, useMemo, useRef, useState } from "react";
import { useContext } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { ReactComponent as AdminsIcon } from "../assets/images/icons/ic-admins.svg";
import { ReactComponent as StudentsIcon } from "../assets/images/icons/ic-students-active.svg";
import { ReactComponent as TeachersIcon } from "../assets/images/icons/ic-teachers-active.svg";
import { ReactComponent as TutorsIcon } from "../assets/images/icons/ic-tutor-active.svg";
import { ReactComponent as ArrowIcon } from "../assets/images/icons/ic-sort-arrow.svg";
import { CompanyType, Role, SortDirection, UserPages } from "../common/constants";
import MainContext from "../common/MainContext";
import { canAccessPage } from "../common/permissions";
import Admins from "../components/Admins";
import Companies from "../components/Companies";
import CompanyAdmins from "../components/CompanyAdmins";
import CompanyUsers from "../components/CompanyUsers";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import Select from "../components/Select";
import Students from "../components/Students";
import Teachers from "../components/Teachers";
import { capitalize } from './../utils';
import styles from "./Users.module.css";

const Users = ({ defaultSelectedTab = UserPages.Students }) => {
  const navigate = useNavigate();
  const context = useContext(MainContext)

  const [selectedTab, setSelectedTab] = useState(defaultSelectedTab)
  const [sortDirection, setSortDirection] = useState(SortDirection.Descending)
  const companyAssociation = context?.user?.company_id ?? context?.user?.company?.id ?? context?.user?.company
  const isCompanyB2BTeaching = Boolean(companyAssociation) && context?.user?.company?.type === CompanyType.B2BTeaching

  const tabs = useMemo(() => {
    if (isCompanyB2BTeaching) {
      return [
        { id: UserPages.Students, permissionPage: 'users/students', icon: StudentsIcon, label: 'Studenti', color: 'var(--sf-light-green)', colorRgb: 'var(--sf-light-green-rgb)', route: '/users/students', value: null },
        { id: UserPages.Admins, permissionPage: 'users/admins', icon: AdminsIcon, label: 'Amministratori', color: 'var(--sf-purple)', colorRgb: 'var(--sf-purple-rgb)', route: '/users/admins', value: null },
      ]
    }

    return [
      { id: UserPages.Students, permissionPage: "users/students", icon: StudentsIcon, label: "Studenti", color: "var(--sf-light-green)", colorRgb: "var(--sf-light-green-rgb)", route: `/users/students`, value: null },
      { id: UserPages.Teachers, permissionPage: "users/teachers", icon: TeachersIcon, label: "Docenti", color: "var(--tertiary)", colorRgb: "var(--tertiary-rgb)", route: `/users/teachers`, value: null },
      { id: UserPages.Tutors, permissionPage: "users/tutors", icon: TutorsIcon, label: "Tutor", color: "var(--sf-blue)", colorRgb: "var(--sf-blue-rgb)", route: `/users/tutors`, value: null },
      { id: UserPages.Admins, permissionPage: "users/admins", icon: AdminsIcon, label: "Admin", color: "var(--sf-purple)", colorRgb: "var(--sf-purple-rgb)", route: `/users/admins`, value: null },
      { id: UserPages.Companies, permissionPage: "users/companies", icon: null, label: "Aziende", color: "var(--sf-orange)", colorRgb: "var(--sf-orange-rgb)", route: `/users/companies`, value: null },
    ]
  }, [isCompanyB2BTeaching])

  const allowedTabs = useMemo(() => {
    if (!context?.user) {
      return []
    }

    return tabs.filter((tab) => canAccessPage(context.user, tab.permissionPage, 'view'))
  }, [tabs, context?.user])

  const orderByOptions = useMemo(() => {
    return [
      { id: 0, name: "Data iscrizione", value: "created_at" },
      { id: 1, name: "Nome", value: "name" },
      { id: 2, name: "Cognome", value: "surname" },
    ]
  }, [])
  const [orderBy, setOrderBy] = useState(orderByOptions[0])

  const studentsRef = useRef(null)
  const teachersRef = useRef(null)
  const tutorsRef = useRef(null)
  const adminsRef = useRef(null)
  const companiesRef = useRef(null)

  useEffect(() => {
    const requestedTabAllowed = allowedTabs.find((tab) => tab.id === defaultSelectedTab)
    if (requestedTabAllowed) {
      setSelectedTab(defaultSelectedTab)
      return
    }

    if (allowedTabs.length > 0) {
      setSelectedTab(allowedTabs[0].id)
      navigate(allowedTabs[0].route, { replace: true })
    }
  }, [defaultSelectedTab, allowedTabs, navigate])


  const handleAddClick = () => {
    switch (selectedTab) {
      case UserPages.Students: studentsRef.current?.onAddClick(); break;
      case UserPages.Teachers: teachersRef.current?.onAddClick(); break;
      case UserPages.Tutors: tutorsRef.current?.onAddClick(); break;
      case UserPages.Admins: adminsRef.current?.onAddClick(); break;
      case UserPages.Companies: companiesRef.current?.onAddClick(); break;
      default: break;
    }
  }

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>
            {capitalize(allowedTabs.find((tab) => tab.id === selectedTab)?.label || "Utenti")}
          </title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <header className={styles.pageHeader}>
              <div><h1>Utenti</h1><p>{isCompanyB2BTeaching ? "Gestisci gli studenti e gli accessi della tua azienda." : "Gestisci profili, accessi e organizzazioni."}</p></div>
              {isCompanyB2BTeaching && selectedTab === UserPages.Students && <CreateButton onClick={handleAddClick}>Invita utente</CreateButton>}
              {!isCompanyB2BTeaching && (
                <CreateButton onClick={handleAddClick} disabled={allowedTabs.length === 0}>
                   Aggiungi
                </CreateButton>
              )}
            </header>
            <div className={styles.header}>
              <nav className={styles.viewSwitch} aria-label="Categorie utenti">
                {allowedTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`${styles.viewButton} ${selectedTab === tab.id ? styles.viewButtonActive : ''}`}
                    onClick={() => {
                      setSelectedTab(tab.id)
                      navigate(tab.route, { replace: true })
                    }}
                    style={{'--tab-color': tab.colorRgb}}
                    aria-pressed={selectedTab === tab.id}
                  >
                    {tab.icon && <tab.icon aria-hidden="true" />}{tab.label}
                  </button>
                ))}
              </nav>
              {!isCompanyB2BTeaching && (
                <div className={styles.orderSelector}>
                  <span className={styles.orderLabel}>Ordina per</span>
                  <Select appearance="modern" portal
                    style={{ minWidth: "172px" }}
                    options={orderByOptions}
                    selected={orderBy}
                    onSelect={(o) => setOrderBy(o)}
                  />
                  <button type="button" className={styles.sortDirection} aria-label={sortDirection === SortDirection.Ascending ? "Ordine crescente: inverti" : "Ordine decrescente: inverti"} onClick={() => setSortDirection(sd => sd === SortDirection.Ascending ? SortDirection.Descending : SortDirection.Ascending)}>
                    <ArrowIcon
                      className={sortDirection === SortDirection.Ascending ? styles.up : styles.down} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
            {selectedTab === UserPages.Students && !isCompanyB2BTeaching && <Students ref={studentsRef} orderBy={orderBy} sortDirection={sortDirection} />}
            {selectedTab === UserPages.Students && isCompanyB2BTeaching && <CompanyUsers ref={studentsRef} />}
            {selectedTab === UserPages.Teachers && <Teachers ref={teachersRef} role={Role.Teacher} orderBy={orderBy} sortDirection={sortDirection} />}
            {selectedTab === UserPages.Tutors && <Teachers ref={tutorsRef} role={Role.Tutor} orderBy={orderBy} sortDirection={sortDirection} />}
            {selectedTab === UserPages.Admins && !isCompanyB2BTeaching && <Admins ref={adminsRef} orderBy={orderBy} sortDirection={sortDirection} />}
            {selectedTab === UserPages.Admins && isCompanyB2BTeaching && <CompanyAdmins ref={adminsRef} />}
            {selectedTab === UserPages.Companies && <Companies ref={companiesRef} orderBy={orderBy} sortDirection={sortDirection} />}
          </div>
        </div>
      </div>
    </HeaderFooterLayout>
  );
};

export default Users;
